import React from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Customer, CreateCustomerDTO } from '../../types/customer';

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (data: CreateCustomerDTO) => void;
  onClose: () => void;
}

export default function CustomerForm({ customer, onSubmit, onClose }: CustomerFormProps) {
  const { control, handleSubmit } = useForm<CreateCustomerDTO>({
    defaultValues: customer
      ? {
          firstName: customer.firstName,
          lastName: customer.lastName || undefined,
          email: customer.email || undefined,
          phone: customer.phone,
          dob: customer.dob || undefined,
          streetOne: customer.streetOne || undefined,
          streetTwo: customer.streetTwo || undefined,
          city: customer.city || undefined,
          state: customer.state || undefined,
          postalCode: customer.postalCode || undefined,
          notes: customer.notes || undefined,
          allowExtraTime: customer.allowExtraTime || false,
        }
      : {
          firstName: '',
          phone: '',
          allowExtraTime: false,
        },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogTitle>{customer ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: 'First name is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="First Name"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => <TextField {...field} label="Last Name" fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Email"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Phone (Optional)"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="streetOne"
              control={control}
              render={({ field }) => <TextField {...field} label="Address Line 1" fullWidth />}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="streetTwo"
              control={control}
              render={({ field }) => <TextField {...field} label="Address Line 2" fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="city"
              control={control}
              render={({ field }) => <TextField {...field} label="City" fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              name="state"
              control={control}
              render={({ field }) => <TextField {...field} label="State" fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => <TextField {...field} label="Postal Code" fullWidth />}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Notes" multiline rows={3} fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="allowExtraTime"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={e => field.onChange(e.target.checked)}
                    />
                  }
                  label="Allow Extra Time"
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disableElevation>
          {customer ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </form>
  );
}
